import {IconCheck, IconPencil, IconX} from "@tabler/icons-react";
import {notification} from "@ui";
import {useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import useCheckAlias from "../../hooks/useCheckAlias";
import useGetUserInfo from "../../hooks/useGetUserInfo";
import useUpdateAlias from "../../hooks/useUpdateAlias";
import {useUserSupport} from "../../store/useUserSupport.store";
import styles from "./UserProfile.module.css";
import UserProfileAvatar from "./UserProfileAvatar";

const UserProfile = () => {
  const {data} = useGetUserInfo();
  const {t} = useTranslation();

  const handleSupportModal = useUserSupport(state => state.handleSupportModal);
  const isMobile = window.innerWidth <= 600;
  const avatarSize = isMobile ? 80 : 120;

  const [isEditingAlias, setIsEditingAlias] = useState(false);
  const [aliasInput, setAliasInput] = useState("");
  const [aliasToCheck, setAliasToCheck] = useState("");
  const aliasTimerRef = useRef(null);

  const {isAliasAvailable, isLoading: isCheckingAlias} = useCheckAlias(aliasToCheck, aliasToCheck.length >= 3);
  const {updateAliasAsync, isPending: isSavingAlias, reset: resetAliasMutation} = useUpdateAlias();

  const handleAliasInputChange = e => {
    const value = e.target.value.toLowerCase().replace(/[^a-z0-9._-]/g, "");
    setAliasInput(value);
    clearTimeout(aliasTimerRef.current);
    if (value.length >= 3 && value !== data?.alias) {
      aliasTimerRef.current = setTimeout(() => setAliasToCheck(value), 500);
    } else {
      setAliasToCheck("");
    }
  };

  const handleStartEditAlias = () => {
    setAliasInput(data?.alias || "");
    setAliasToCheck("");
    resetAliasMutation();
    setIsEditingAlias(true);
  };

  const handleCancelEditAlias = () => {
    setIsEditingAlias(false);
    setAliasInput("");
    setAliasToCheck("");
  };

  const handleSaveAlias = async () => {
    if (!aliasInput || aliasInput.length < 3 || aliasInput === data?.alias) return;
    if (!isAliasAvailable || isCheckingAlias) return;
    try {
      await updateAliasAsync(aliasInput);
      setIsEditingAlias(false);
      setAliasInput("");
      setAliasToCheck("");
      notification.success({
        message: t("user_alias_success"),
        duration: 3,
      });
    } catch {
      notification.error({
        message: t("user_alias_error"),
        duration: 3,
      });
    }
  };

  const handleAvatarUpload = _file => {};

  return (
    <div className={styles.profile_info_container}>
      <div className={styles.profile_avatar}>
        <UserProfileAvatar
          user={data}
          size={avatarSize}
          onUpload={handleAvatarUpload}
        />
      </div>

      <div className={styles.profile_info}>
        <h4>{t("user_alias")}:</h4>
        {isEditingAlias ? (
          <div className={styles.aliasEditGroup}>
            <div className={styles.aliasInputWrapper}>
              <span className={styles.aliasAtSign}>@</span>
              <input
                type="text"
                className={styles.aliasInput}
                value={aliasInput}
                onChange={handleAliasInputChange}
                placeholder={t("user_alias_placeholder")}
                maxLength={30}
                disabled={isSavingAlias}
              />
            </div>
            {aliasInput.length > 0 && aliasInput.length < 3 && (
              <span className={styles.aliasHint}>{t("user_alias_min_length")}</span>
            )}
            {aliasInput.length >= 3 && aliasInput !== data?.alias && (
              <span
                className={
                  isCheckingAlias
                    ? styles.aliasHint
                    : isAliasAvailable
                      ? styles.aliasAvailable
                      : styles.aliasUnavailable
                }
              >
                {isCheckingAlias
                  ? t("user_alias_checking")
                  : isAliasAvailable
                    ? t("user_alias_available")
                    : t("user_alias_unavailable")}
              </span>
            )}
            <div className={styles.aliasActions}>
              <button
                type="button"
                className={styles.aliasCancelBtn}
                onClick={handleCancelEditAlias}
                disabled={isSavingAlias}
              >
                <IconX size={14} />
                {t("user_alias_cancel")}
              </button>
              <button
                type="button"
                className={styles.aliasSaveBtn}
                onClick={handleSaveAlias}
                disabled={
                  isSavingAlias ||
                  isCheckingAlias ||
                  !isAliasAvailable ||
                  aliasInput.length < 3 ||
                  aliasInput === data?.alias
                }
              >
                <IconCheck size={14} />
                {isSavingAlias ? t("user_alias_saving") : t("user_alias_save")}
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            className={styles.aliasValueBtn}
            onClick={handleStartEditAlias}
            aria-label={t("user_alias_edit")}
          >
            <h4 className={styles.profile_info_value}>@{data?.alias || "—"}</h4>
            <IconPencil
              size={14}
              className={styles.aliasEditIcon}
            />
          </button>
        )}
      </div>

      <div className={styles.profile_info}>
        <h4>{t("user_email")}: </h4>
        <h4 className={styles.profile_info_value}>{data?.email}</h4>
      </div>

      <div className={styles.profile_info}>
        <h4>{t("user_phoneNumber")}: </h4>
        <h4 className={styles.profile_info_value}>{data?.phoneNumber}</h4>
      </div>

      <div className={styles.profile_info}>
        <h4>{t("user_birthdate")}: </h4>
        <h4 className={styles.profile_info_value}>{data?.birthdate}</h4>
      </div>

      <div className={styles.profile_info}>
        <h4>{t("user_national_id")}: </h4>
        <h4 className={styles.profile_info_value}>{data?.nationalID}</h4>
      </div>

      <div className={styles.profile_info}>
        <h4>{t("user_state")}: </h4>
        <h4 className={styles.profile_info_value}>{data?.state}</h4>
      </div>

      <div className={styles.profile_info}>
        <h4>{t("user_city")}: </h4>
        <h4 className={styles.profile_info_value}>{data?.city}</h4>
      </div>
      <p>
        {t("user_support_message")} <a onClick={handleSupportModal}>{t("user_support_link")}</a>
      </p>
    </div>
  );
};

export default UserProfile;
